import { Pagination as HeroPagination, SharedSelection } from "@heroui/react";
import clsx from "clsx";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { useEffect, useMemo } from "react";
import { useTranslations } from "translations";
import { Select } from "../select/select";
import type { PaginationProps, RowSelectProps } from "./types";
import {
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  commonClasses,
} from "./utils";

const RowsSelection = ({
  onRowsPerPageChange,
  rowsPerPage,
  rowsPerPageOptions,
  variant,
}: RowSelectProps) => {
  const translations = useTranslations();

  const selectItems = useMemo(
    () =>
      rowsPerPageOptions.map((value) => ({
        id: String(value),
        label: String(value),
      })),
    [rowsPerPageOptions],
  );

  const onSelectionChange = (keys: SharedSelection) => {
    if (keys) {
      const selectedKey = Array.from(keys)[0];
      const newRowsPerPage = Number(selectedKey);
      onRowsPerPageChange(newRowsPerPage);
    }
  };

  return (
    <div className="flex min-w-45 items-center gap-2">
      <Select
        items={selectItems}
        label={translations.predict.dashboard.pagination}
        renderItem={(item) => item.label}
        selectedKeys={[String(rowsPerPage)]}
        size="sm"
        variant={variant}
        onSelectionChange={onSelectionChange}
      />
    </div>
  );
};

export function Pagination({
  isCentered,
  onPageChange,
  onRowsPerPageChange,
  page,
  rowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  selectVariant = "horizontal",
  showRowsPerPage,
  total,
  ...props
}: PaginationProps) {
  const translations = useTranslations();

  // When the rows-per-page selector is hidden, callers may omit `rowsPerPage`;
  // fall back to the default so totalPages math stays meaningful.
  const effectiveRowsPerPage = rowsPerPage ?? DEFAULT_ROWS_PER_PAGE;

  const totalPages = useMemo(() => {
    if (total === undefined || total <= 0) return 0;
    return Math.ceil(total / effectiveRowsPerPage);
  }, [total, effectiveRowsPerPage]);

  // Defensive clamp of the displayed page (preserves prior behaviour):
  const displayedPage = useMemo(() => {
    if (totalPages === 0) return 1;
    return Math.max(1, Math.min(page, totalPages));
  }, [page, totalPages]);

  const { updateA11yMessage } = useA11yMessage();

  useEffect(() => {
    if (total && totalPages > 0) {
      updateA11yMessage(
        translations.accessibility.paginationCount({
          page: `${displayedPage}`,
          totalPages: `${totalPages}`,
        }),
      );
    }
  }, [displayedPage, totalPages, total, updateA11yMessage]);

  if (total === undefined || total <= 0 || totalPages === 0) return null;

  return (
    <div
      className={clsx("flex flex-col items-center tablet:flex-row", {
        "justify-between gap-4": !isCentered,
        "justify-center gap-6": isCentered,
      })}
    >
      {showRowsPerPage && (
        <RowsSelection
          rowsPerPage={effectiveRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          variant={selectVariant}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
      <HeroPagination
        {...props}
        classNames={{
          chevronNext: clsx("text-gray-900", "forced-colors:text-[ButtonText]"),
          cursor: clsx(
            "forced-colors:bg-[SelectedItem]",
            "forced-colors:text-[SelectedItemText]",
            "forced-colors:border-[SelectedItemText]",
          ),
          ellipsis: clsx("text-gray-900", "forced-colors:text-[ButtonText]"),
          forwardIcon: clsx("text-gray-500", "forced-colors:text-[ButtonText]"),
          item: commonClasses(props.isCompact),
          next: clsx(
            commonClasses(props.isCompact),
            "data-[disabled=true]:bg-gray-200 data-[disabled=true]:text-gray-900",
            "forced-colors:data-[disabled=true]:text-[GrayText]",
            "forced-colors:data-[disabled=true]:border-[GrayText]",
          ),
          prev: clsx(
            commonClasses(props.isCompact),
            "data-[disabled=true]:bg-gray-200 data-[disabled=true]:text-gray-900",
            "forced-colors:data-[disabled=true]:text-[GrayText]",
            "forced-colors:data-[disabled=true]:border-[GrayText]",
          ),
          wrapper: clsx(
            "shadow-none",
            "forced-colors:bg-[Canvas]",
            {
              "bg-gray-400": props.isCompact,
              "forced-colors:bg-[GrayText]!": props.isCompact,
            },
            {
              "forced-colors:bg-[Canvas]!": !props.isCompact,
            },
          ),
        }}
        page={displayedPage}
        total={totalPages}
        onChange={onPageChange}
      />
    </div>
  );
}

Pagination.displayName = "RecareUI.Pagination";
export { PaginationProps };
